import { Link } from "@tanstack/react-router";
import { Star, MapPin, Clock } from "lucide-react";
import { track } from "@/lib/analytics";
import { getOpenStatus } from "@/lib/openHours";
import type { Salon } from "@/data/types";
import { SalonRatingLines } from "@/components/SalonRatingLines";
import { internalPath } from "@/lib/paths";
import { withBookingUtm } from "@/lib/booking";

export function SalonCard({ salon }: { salon: Salon }) {
  const status = getOpenStatus(salon);
  return (
    <article className="card-elev overflow-hidden flex">
      <Link
        to={internalPath(salon.url)}
        onClick={() => track("salon_select", { salon_name: salon.shortName, cta_location: "salon_card" })}
        className="block w-28 sm:w-36 shrink-0 bg-muted"
      >
        <img
          src={salon.imageThumb ?? salon.image}
          alt={`Interiör av ${salon.name}`}
          width={320}
          height={240}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
        />
      </Link>
      <div className="p-3 sm:p-4 flex flex-col gap-2 flex-1 min-w-0">
        <div className="flex items-start gap-2 text-xs text-muted-foreground flex-wrap">
          <Star size={12} className="fill-brass text-brass" />
          <SalonRatingLines salon={salon.slug} className="text-foreground" />
          <span
            className={`ml-auto inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
              status.open
                ? "bg-emerald-500/15 text-emerald-700"
                : "bg-muted text-muted-foreground"
            }`}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${status.open ? "bg-emerald-500" : "bg-muted-foreground/50"}`} />
            {status.open ? "Öppet nu" : "Stängt"}
          </span>
        </div>
        <h3 className="text-base sm:text-lg leading-tight">{salon.shortName}</h3>
        <p className="text-xs text-muted-foreground flex items-center gap-1 truncate">
          <MapPin size={12} className="shrink-0" /> {salon.address}
        </p>
        {status.todayHours ? (
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <Clock size={12} className="shrink-0" />
            {status.open
              ? <>Idag {status.todayHours}</>
              : status.nextOpen
                ? <>Öppnar {status.nextOpen.day} {status.nextOpen.hours}</>
                : <>Idag {status.todayHours}</>}
          </p>
        ) : null}
        <div className="mt-auto flex flex-row flex-nowrap gap-2 pt-2">
          <a
            href={withBookingUtm(salon.bokaDirektUrl)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track("book_now_click", { salon_name: salon.shortName, cta_location: "salon_card" })}
            className="btn-brass flex-1 min-w-0 text-sm px-3 py-2.5 font-semibold whitespace-nowrap"
          >
            Boka tid
          </a>
          <Link
            to={internalPath(salon.url)}
            className="btn-outline shrink-0 text-xs px-3 py-2.5 whitespace-nowrap"
          >
            Läs mer<span className="sr-only"> om {salon.shortName}</span>
          </Link>
        </div>
      </div>
    </article>
  );
}
